import { useLocation, useHistory } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import { convertQueryToString, convertStringBackToQuery } from '../../../helpers/queryString';
import { defaultFilters, isDefaultFilters } from '../defaultFilters';

const defaultSort = {
  sortBy: 'date_from',
  sortDirection: 'desc',
};

export const useHiresSearchParam = (defaultQueryFilters = { filters: defaultFilters }) => {
  const { search } = useLocation();
  const { push } = useHistory();
  const [page, setPage] = useState(1);

  const params = useMemo(() => {
    const { filters, ...others } = search ? convertStringBackToQuery(search) : defaultQueryFilters;

    // Create a stable object reference to prevent unnecessary re-renders
    const mergedFilters = { ...defaultQueryFilters.filters, ...filters };

    return {
      ...defaultQueryFilters,
      ...others,
      filters: mergedFilters,
    };
  }, [search, defaultQueryFilters]);

  const setSearchParams = useCallback(
    (value) => {
      let newValues = { ...value };
      if (typeof value === 'function') {
        newValues = value(params);
      }

      setPage((current) => {
        if (newValues.page && current !== newValues.page) {
          return newValues.page;
        }
        return current;
      });

      delete newValues.page;

      // Clear URL if filters are at default state
      const isDefault =
        isDefaultFilters(newValues.filters) &&
        ((typeof newValues.filters?.sortBy === 'undefined' &&
          typeof newValues.filters?.sortDirection === 'undefined') ||
          (newValues.filters?.sortBy === defaultSort.sortBy &&
            newValues.filters?.sortDirection === defaultSort.sortDirection));

      push({
        search: isDefault ? '' : convertQueryToString(newValues),
      });

      return newValues;
    },
    [params, push],
  );

  // Memoize the returned array to prevent unnecessary re-renders
  const result = useMemo(() => [{ ...params, page }, setSearchParams], [params, page, setSearchParams]);

  return result;
};
